// Types
import type { Ref } from 'vue';
import type {
  TStoredFilters,
  TFilterTimestamps,
  TWrappedFilters,
  TUnwrappedFilters,
} from '~/misc/listings/types';

// Utils
import { unwrapFilters, wrapFilters } from '~/misc/listings/utils/filter-wrapper';

/**
 * Tracks per-field timestamps so filter save requests carry accurate
 * `updated_at` values and only changed fields receive a new timestamp.
 *
 * Internally keeps three pieces of state:
 * - `timestamps`  – last-known `updated_at` per filter key.
 * - `lastLoaded`  – flat filter values used as the change-detection baseline.
 * - `lastWrapped` – full wrapped GET response used as the merge base for saves.
 *
 * Typical lifecycle:
 * 1. `unwrapResponse(apiResponse)` – on GET, stores baseline + timestamps.
 * 2. `updateBaseline(storedFilters)` – aligns baseline with UI-normalised shape.
 * 3. `wrapForSave(currentFilters)` – merges GET response with changed fields.
 * 4. `syncSaved(currentFilters, wrappedPayload)` – after PATCH, updates state.
 * 5. `resetTimestamps()` – clears all state (e.g. when filters are deleted).
 *
 * @returns unwrapResponse  - Unwraps a wrapped API response into flat filters
 *                            and timestamps, storing both as the new baseline.
 * @returns wrapForSave     - Builds the PATCH payload by merging the original
 *                            GET response with only the changed fields.
 * @returns syncSaved       - Updates internal state after a successful save so
 *                            subsequent saves detect changes correctly.
 * @returns updateBaseline  - Replaces the flat baseline with the current filter
 *                            state (use after UI normalisation adds/removes keys).
 * @returns resetTimestamps - Clears all tracked state.
 */
const useFilterTimestamps = () => {
  const timestamps: Ref<TFilterTimestamps> = ref<TFilterTimestamps>({});
  const lastLoaded: Ref<TStoredFilters | null> = ref<TStoredFilters | null>(null);
  const lastWrapped: Ref<TWrappedFilters | null> = ref<TWrappedFilters | null>(null);

  const setTimestamps = (newTimestamps: TFilterTimestamps): void => {
    timestamps.value = newTimestamps;
  };

  const setLastLoaded = (newLastLoaded: TStoredFilters | null): void => {
    lastLoaded.value = newLastLoaded;
  };

  const setLastWrapped = (newLastWrapped: TWrappedFilters | null): void => {
    lastWrapped.value = newLastWrapped;
  };

  /** Unwraps API response, stores timestamps, baseline and raw wrapped response. */
  const unwrapResponse = (wrapped: TWrappedFilters): TUnwrappedFilters => {
    const result: TUnwrappedFilters = unwrapFilters(wrapped);

    setTimestamps(result.timestamps);
    setLastLoaded(result.filters);
    setLastWrapped({ ...wrapped });

    // For convenience, also return the unwrapped filters and timestamps
    // so callers don't need to access internal state immediately after.
    return result;
  };

  /** Wraps current filters for saving — merges original GET response with changes. */
  const wrapForSave = (current: TStoredFilters): TWrappedFilters => {
    const changed: TWrappedFilters = wrapFilters(current, timestamps.value, lastLoaded.value, {
      onlyChanged: true,
    });

    return lastWrapped.value ? { ...lastWrapped.value, ...changed } : changed;
  };

  /** Syncs tracked state after a successful HTTP save. */
  const syncSaved = (current: TStoredFilters, wrapped: TWrappedFilters): void => {
    setLastWrapped({ ...wrapped });

    const keys = Object.keys(wrapped) as (keyof TWrappedFilters)[];

    for (const key of keys) {
      const field: TWrappedFilters[keyof TWrappedFilters] = wrapped[key];

      if (field) timestamps.value[key] = field.updated_at;
    }

    if (lastLoaded.value) {
      const currentKeys = Object.keys(current) as (keyof TStoredFilters)[];

      for (const key of currentKeys) {
        (lastLoaded.value as Record<string, unknown>)[key] = current[key];
      }
    } else {
      setLastLoaded(current);
    }
  };

  /** Aligns baseline with the actual filter state after UI normalization. */
  const updateBaseline = (current: TStoredFilters): void => {
    setLastLoaded(current);
  };

  /** Clears timestamps and baseline (e.g. when no saved filters exist). */
  const resetTimestamps = (): void => {
    setTimestamps({});
    setLastLoaded(null);
    setLastWrapped(null);
  };

  return { unwrapResponse, wrapForSave, syncSaved, updateBaseline, resetTimestamps };
};

export { useFilterTimestamps };
